// Logbook entries as a read-only page should DRAW them, plus the gate that
// decides whether there is a map at all.
//
//   displayEntries - each route resolved against the gazetteer, so a place
//                    moved in /me/places moves here too, and the From/To in
//                    the blob re-derived from that route so a place RENAMED
//                    reaches the page as well as the map. Those blob values
//                    are a save-time mirror and resolvePlaceName can only
//                    match a row by the name in them, so the waypoint's
//                    placeId is the only way back to a renamed row.
//   mapLegs        - the adapter TripGroupMap wants: `from`/`to` hoisted out
//                    of the blob so backfillEndpointNames can fill an unnamed
//                    anchor, `fields` passed straight through so nothing
//                    outside TripRoute parses it.
//   newestFirst    - displayEntries reversed, for a TABLE. The input is
//                    oldest-first because the map needs it that way (the End
//                    pill hangs on the chain's LAST leg and leg colours cycle
//                    by index), but a list of entries reads newest-first
//                    everywhere else in the app, so the trip page and the
//                    shared trip page draw their rows from this one instead.
//                    Reversing the mirror-then-sort list also restores the
//                    query's own DESC order for legs sharing one date, so the
//                    rows match the logbook list exactly.
//   anyOnMap       - gates the map card AND the lazy chunk behind it. With
//                    nothing placed there is nothing to draw, and rendering
//                    the Suspense anyway downloads maplibre-gl (~250 KB gz)
//                    for a card that shows nothing.
//
// Strictly read-only: these copies must never reach a write path. A caller
// opening an entry re-looks-up the STORED row by id instead -- a pre-resolved
// handover would seed the entry page with a route its own resolve then agrees
// with, leaving routeGen at 0 and silently skipping that navigation's lazy
// write-back.

import { useMemo } from 'react';
import { parseEntryFields } from '../../fleet-shared/LogbookTypes.mjs';
import {
  ENTRY_ROUTE_KEY,
  routeFromFields,
  entryWithResolvedEndpoints,
  hasPlacedWaypoints,
} from '../../fleet-shared/TripRoute.mjs';

export function useEntryMapLegs(orderedEntries, placeIndex) {
  const displayEntries = useMemo(
    () =>
      orderedEntries
        ? orderedEntries.map((entry) => entryWithResolvedEndpoints(entry, placeIndex))
        : null,
    [orderedEntries, placeIndex]
  );

  const mapLegs = useMemo(() => {
    if (!displayEntries) return null;
    return displayEntries.map((entry) => {
      const json = parseEntryFields(entry);
      return { id: entry.id, from: json.from, to: json.to, fields: entry.fields };
    });
  }, [displayEntries]);

  const newestFirst = useMemo(
    () => (displayEntries ? [...displayEntries].reverse() : null),
    [displayEntries]
  );

  const anyOnMap = useMemo(
    () => (mapLegs ?? []).some((leg) => hasPlacedWaypoints(routeFromFields(leg.fields, ENTRY_ROUTE_KEY))),
    [mapLegs]
  );

  return { displayEntries, newestFirst, mapLegs, anyOnMap };
}

export default useEntryMapLegs;
